/**
 * HoldingsPanel Component - 보유 종목 카드 패널 (실시간 전략 신호 통합)
 */
import React, { useCallback, useEffect, useState } from 'react';
import type { AIStockData } from '../types/watchlist';
import StockCard from './StockCard';
import { fetchHoldingsData, AccountSummary, AccountInfo } from './HoldingsPanelHelpers';
import './HoldingsPanel.css';

interface HoldingsPanelProps {
  onExecute: (ticker: string) => void;
  executingSymbol: string | null;
}

const HoldingsPanel: React.FC<HoldingsPanelProps> = ({ onExecute, executingSymbol }) => {
  const [holdings, setHoldings] = useState<AIStockData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);

  const fetchHoldings = useCallback(async (isMounted: () => boolean) => {
    try {
      await fetchHoldingsData(isMounted, setHoldings, setAccountInfo, setError, setLoading);
    } catch (err: any) {
      console.error('보유 종목 조회 실패:', err);
      if (isMounted()) {
        setError(err.message);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const isMounted = () => !cancelled;
    fetchHoldings(isMounted);
    return () => { cancelled = true; };
  }, [fetchHoldings]);

  if (loading && holdings.length === 0) {
    return (
      <div className="holdings-panel">
        <h3>보유 종목</h3>
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  if (error && holdings.length === 0) {
    return (
      <div className="holdings-panel">
        <h3>보유 종목</h3>
        <div className="error">오류: {error}</div>
      </div>
    );
  }

  if (holdings.length === 0) {
    return (
      <div className="holdings-panel">
        <h3>보유 종목 (0개)</h3>
        <div className="empty">현재 보유 중인 종목이 없습니다</div>
      </div>
    );
  }

  return (
    <div className="holdings-panel">
      <div className="panel-header">
        <h3>보유 종목 ({holdings.length}개)</h3>
        {accountInfo && <AccountSummary accountInfo={accountInfo} />}
      </div>
      <div className="holdings-grid">
        {holdings.map((holding) => (
          <StockCard
            key={holding.ticker}
            stock={holding}
            onExecute={onExecute}
            executing={executingSymbol === holding.ticker}
            variant="holding"
          />
        ))}
      </div>
    </div>
  );
};

export default HoldingsPanel;
